import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import { Button, Popover } from '@heroui/react';
import type { OperationText } from '@/api/models';
import { resolveLocale } from '@/pages/accounting/statements/statementMeta';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';

type OperationRecipeProps = {
    /** The operation's name, for the popover heading and the trigger's label. */
    name: string;
    steps: OperationText[];
    /** Row discriminator, appended after the literal testid prefix. */
    slot: string;
};

/**
 * What a built-in operation does to the books, in words.
 *
 * This is the only thing a system row offers, and it is the reason those rows
 * are in the catalog at all: an OrgAdmin should be able to find out what a sale
 * posts without reading the journal and reverse-engineering it from an entry.
 *
 * Shown in the interface language alone — same as every other label on this
 * screen — rather than both locales at once.
 *
 * A popover rather than an expanding row: `DataGrid` owns its own `<tbody>`, and
 * the recipe is reference material read once, not a column anyone scans.
 */
export const OperationRecipe: React.FC<OperationRecipeProps> = ({
    name,
    steps,
    slot,
}) => {
    const { t, i18n } = useTranslation('pages');
    const locale = resolveLocale(i18n.language);

    return (
        <Popover>
            <Popover.Trigger
                aria-label={t('operations.recipe.openNamed', { name })}
            >
                <Button
                    size="sm"
                    variant="ghost"
                    {...getNodeTestIdProp(`operations-recipe-trigger-${slot}`)}
                >
                    <Icon aria-hidden icon="gravity-ui:circle-info" />
                    {t('operations.recipe.open')}
                </Button>
            </Popover.Trigger>
            <Popover.Content className="w-[min(30rem,calc(100vw-2rem))]">
                <Popover.Dialog>
                    <Popover.Heading>
                        {t('operations.recipe.heading', { name })}
                    </Popover.Heading>
                    <ol
                        className="mt-3 flex flex-col gap-3"
                        {...getNodeTestIdProp(
                            `operations-recipe-steps-${slot}`,
                        )}
                    >
                        {steps.map((step, index) => (
                            <li
                                className="flex gap-2 text-sm"
                                key={`${step.en}-${index}`}
                            >
                                <span className="shrink-0 tabular-nums text-muted">
                                    {index + 1}.
                                </span>
                                <span className="text-foreground">
                                    {step[locale]}
                                </span>
                            </li>
                        ))}
                    </ol>
                    <p className="mt-3 text-xs text-muted">
                        {t('operations.recipe.note')}
                    </p>
                </Popover.Dialog>
            </Popover.Content>
        </Popover>
    );
};
